import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { btnPrimary, inputDark } from "@/components/shared/AdminKit";
import { PlatformIcon } from "@/components/shared/Badges";
import { useTr } from "@/lib/tr";

const ICONS = ["globe", "instagram", "youtube", "music", "send"];

function Lbl({ children, req }) {
  return <span className="mb-1 block text-xs text-slate-400">{children}{req && <span className="text-red-400"> *</span>}</span>;
}

function Shell({ open, onClose, title, desc, testid, children, onSubmit, busy, tr }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md border-slate-800 bg-slate-900 text-white" data-testid={testid}>
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle><DialogDescription className="text-slate-400">{desc}</DialogDescription></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); onSubmit(); }} className="space-y-3 text-sm">
          {children}
          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            <button type="button" onClick={onClose} className="rounded-full border border-slate-700 px-5 py-2.5 hover:bg-slate-800" data-testid={`${testid}-cancel`}>{tr("Annuler", "Cancel")}</button>
            <button disabled={busy} className={btnPrimary + " justify-center"} data-testid={`${testid}-submit`}>{busy && <Loader2 className="h-4 w-4 animate-spin" />}{tr("Créer", "Create")}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ActiveToggle({ value, onChange, testid, tr }) {
  return (
    <label className="flex items-center gap-2"><input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4" data-testid={testid} />{tr("Active", "Active")}</label>
  );
}

export function QuickPlatformDialog({ open, onClose, onCreated, count }) {
  const tr = useTr();
  const [f, setF] = useState({ name: "", name_en: "", icon: "globe", active: true });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name.trim()) { toast.error(tr("Le nom de la plateforme est obligatoire", "Platform name is required")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/platforms", { ...f, sort_order: count });
      toast.success(tr("Plateforme créée", "Platform created"));
      setF({ name: "", name_en: "", icon: "globe", active: true });
      onCreated(data.platform);
    } catch (e) { toast.error(e.response?.status === 409 ? tr("Cette plateforme existe déjà", "This platform already exists") : apiError(e.response?.data?.detail) || tr("Création impossible", "Creation failed")); } finally { setBusy(false); }
  };
  return (
    <Shell open={open} onClose={onClose} busy={busy} onSubmit={submit} tr={tr} testid="quick-platform-dialog"
      title={tr("Créer une plateforme", "Create a platform")} desc={tr("Elle sera sélectionnée dans le service en cours.", "It will be selected in the current service.")}>
      <label className="block"><Lbl req>{tr("Nom de la plateforme", "Platform name")}</Lbl><input autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputDark} data-testid="quick-platform-name" /></label>
      <label className="block"><Lbl>{tr("Nom anglais (facultatif)", "English name (optional)")}</Lbl><input value={f.name_en} onChange={(e) => setF({ ...f, name_en: e.target.value })} className={inputDark} data-testid="quick-platform-name-en" /></label>
      <div><Lbl>{tr("Icône", "Icon")}</Lbl>
        <div className="flex flex-wrap gap-2">{ICONS.map((i) => (
          <button type="button" key={i} onClick={() => setF({ ...f, icon: i })} data-testid={`quick-platform-icon-${i}`}
            className={`rounded-lg border p-2.5 transition-colors ${f.icon === i ? "border-accent bg-accent/20" : "border-slate-700 hover:bg-slate-800"}`}><PlatformIcon icon={i} className="h-4 w-4" /></button>
        ))}</div>
      </div>
      <ActiveToggle value={f.active} onChange={(v) => setF({ ...f, active: v })} testid="quick-platform-active" tr={tr} />
    </Shell>
  );
}

export function QuickCategoryDialog({ open, onClose, onCreated, platforms, platformId }) {
  const tr = useTr();
  const [f, setF] = useState({ name: "", name_en: "", sort_order: 0, active: true });
  const [pid, setPid] = useState(platformId || "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name.trim()) { toast.error(tr("Le nom de la catégorie est obligatoire", "Category name is required")); return; }
    if (!pid) { toast.error(tr("Choisissez la plateforme concernée", "Choose the platform")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/categories", { ...f, platform_id: pid, sort_order: Number(f.sort_order) || 0 });
      toast.success(tr("Catégorie créée", "Category created"));
      setF({ name: "", name_en: "", sort_order: 0, active: true });
      onCreated(data.category);
    } catch (e) { toast.error(e.response?.status === 409 ? tr("Cette catégorie existe déjà pour cette plateforme", "This category already exists for this platform") : apiError(e.response?.data?.detail) || tr("Création impossible", "Creation failed")); } finally { setBusy(false); }
  };
  return (
    <Shell open={open} onClose={onClose} busy={busy} onSubmit={submit} tr={tr} testid="quick-category-dialog"
      title={tr("Créer une catégorie", "Create a category")} desc={tr("Elle sera sélectionnée dans le service en cours.", "It will be selected in the current service.")}>
      <label className="block"><Lbl req>{tr("Nom de la catégorie", "Category name")}</Lbl><input autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputDark} data-testid="quick-category-name" /></label>
      <label className="block"><Lbl>{tr("Nom anglais (facultatif)", "English name (optional)")}</Lbl><input value={f.name_en} onChange={(e) => setF({ ...f, name_en: e.target.value })} className={inputDark} data-testid="quick-category-name-en" /></label>
      <label className="block"><Lbl req>{tr("Plateforme", "Platform")}</Lbl>
        <select value={pid} onChange={(e) => setPid(e.target.value)} className={inputDark} data-testid="quick-category-platform">
          <option value="">{tr("Choisir", "Choose")}</option>
          {platforms.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      <div className="grid grid-cols-2 items-end gap-3">
        <label className="block"><Lbl>{tr("Ordre d'affichage", "Display order")}</Lbl><input type="number" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: e.target.value })} className={inputDark} data-testid="quick-category-order" /></label>
        <div className="pb-2.5"><ActiveToggle value={f.active} onChange={(v) => setF({ ...f, active: v })} testid="quick-category-active" tr={tr} /></div>
      </div>
    </Shell>
  );
}
